import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { VisibilityLockIcon } from '@/assets';
import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    restricted: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center'
    },
    header: {
      gap: 8,
      backgroundColor: theme.colors.tertiaryContainer,
      paddingVertical: 8,
      paddingHorizontal: 12
    },
    textHeader: { fontWeight: '700', color: theme.colors.primary }
  });

type RestrictedLabelProps = {
  hiddenForOtherHomeAreas?: boolean;
  geoAreaIds?: string[];
  asInline?: boolean;
  hideDot?: boolean;
};

export const RestrictedLabel: FC<RestrictedLabelProps> = ({
  hiddenForOtherHomeAreas = false,
  geoAreaIds,
  asInline = false,
  hideDot = false
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const { data: geoArea } = useGetGeoArea(geoAreaIds?.[0]);
  // TODO: Klären, wie damit umgegangen werden soll, dass es mehrere geoAreaIds gibt.

  if (asInline)
    return (
      <View style={styles.restricted}>
        {!hideDot && <Text variant="bodyMedium">{'・'}</Text>}
        {hiddenForOtherHomeAreas && (
          <VisibilityLockIcon
            fill={theme.colors.primary}
            width={18}
            height={18}
            style={{ marginRight: 4 }}
          />
        )}
        {geoArea?.name && (
          <Text
            variant="bodyMedium"
            style={{
              color: hiddenForOtherHomeAreas
                ? theme.colors.primary
                : theme.colors.onSurfaceVariant,
              fontWeight: hiddenForOtherHomeAreas ? 700 : 400,
              // Todo: try a different solution if max width solution is not ideal
              maxWidth: 120
            }}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {geoArea?.name}
          </Text>
        )}
      </View>
    );

  return (
    hiddenForOtherHomeAreas && (
      <View style={[styles.restricted, styles.header]}>
        <VisibilityLockIcon
          fill={theme.colors.primary}
          width={32}
          height={32}
        />
        {geoArea?.name && (
          <Text variant="bodyLarge" style={styles.textHeader}>
            {t('onlyIn', { name: geoArea?.name })}
          </Text>
        )}
      </View>
    )
  );
};
